/**
 * PostgreSQL Language Server Context Switcher
 *
 * Due to the shared daemon limitation in postgres-language-server, all LSP instances
 * share the same database schema. This service monitors active tab changes and triggers
 * context switches when needed for EXISTING tabs (new tabs are handled inline in connectToDatabase).
 */

import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../../store/connectionStore';
import { ensureCorrectContext } from './pgLanguageServiceSync';

type PgLsContextSwitchStatus = 'idle' | 'switching' | 'error';

type StatusListener = (status: PgLsContextSwitchStatus) => void;

let lastActiveTabId: string | null = null;
let lastPgContext: { connectionId: string; database: string } | null = null;
let isSwitching = false;
const statusListeners = new Set<StatusListener>();

/**
 * Subscribe to context switch status changes
 */
export const onPgContextSwitchStatus = (listener: StatusListener): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

/**
 * Check if a connection is PostgreSQL
 */
const isPostgresConnection = (connectionId: string): boolean => {
  const { activeConnections } = useConnectionStore.getState();
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  return activeConnection?.connection?.engine === 'pgsql';
};

/**
 * Handle active tab changes - triggers context switch for existing PostgreSQL tabs
 */
export const handleActiveTabChange = async (activeTabId: string | null): Promise<void> => {
  if (!activeTabId || activeTabId === lastActiveTabId) {
    return;
  }

  lastActiveTabId = activeTabId;

  const { getTab } = useWorkspaceStore.getState();
  const tab = getTab(activeTabId);

  if (!tab?.connectionInfo?.connectionId || !tab.connectionInfo.database) {
    return;
  }

  const { connectionId, database } = tab.connectionInfo;

  // Only handle PostgreSQL connections
  if (!isPostgresConnection(connectionId)) {
    return;
  }

  // Check if context is different
  if (lastPgContext && lastPgContext.connectionId === connectionId && lastPgContext.database === database) {
    // Same context, no switch needed
    return;
  }

  // Prevent concurrent switches
  if (isSwitching) {
    console.log('[PgContextSwitcher] Already switching, skipping');
    return;
  }

  console.log(`[PgContextSwitcher] Tab changed to ${connectionId}:${database}, checking if context switch needed`);

  isSwitching = true;
  try {
    // This will check the actual daemon context and switch if needed
    const success = await ensureCorrectContext(connectionId, database);
    if (success) {
      lastPgContext = { connectionId, database };
    }
  } finally {
    isSwitching = false;
  }
};

/**
 * Initialize the context switcher by subscribing to workspace store changes
 */
let unsubscribe: (() => void) | null = null;

export const initializePgContextSwitcher = (): void => {
  if (unsubscribe) {
    return; // Already initialized
  }

  console.log('[PgContextSwitcher] Initializing');

  // Subscribe to activeTabId changes
  unsubscribe = useWorkspaceStore.subscribe((state, prevState) => {
    // Only react to activeTabId changes
    if (state.activeTabId !== prevState.activeTabId) {
      // Use setTimeout to avoid blocking the UI and give SqlMonaco time to connect
      setTimeout(() => {
        handleActiveTabChange(state.activeTabId);
      }, 200);
    }
  });

  // Don't handle initial state - let the normal SqlMonaco connection flow handle it
  // We only need to intervene when switching BETWEEN PostgreSQL databases
};

export const disposePgContextSwitcher = (): void => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  lastActiveTabId = null;
  lastPgContext = null;
  isSwitching = false;
  statusListeners.clear();
};
